// Pure, framework-free display helpers that map the payments connection's
// server-computed `onboardingStatus` onto the two cards' visual states. Kept
// React-free so the state machine is unit-testable in isolation.

import {
  PartyAddress,
  PaymentConnectionResponse,
  PaymentFundingAccount,
  PaymentOnboardingStatus,
} from 'src/queries/payments/payments.types'

/**
 * The three (+ terminal) business-verification stages the KYB card renders,
 * collapsed from the fuller `onboardingStatus` union so the card only reasons
 * about "what does the user see / can do next":
 *  - `unverified` — no usable legal entity yet (never onboarded, or a
 *    disconnected one that must re-onboard). Shows "Start verification".
 *  - `pending`    — under provider review, not actionable by the user: a fresh
 *    KYB submission (`KYB_PENDING`), or a previously-active entity now
 *    `SUSPENDED` (recovers via a provider webhook — NOT a fresh-KYB CTA, which
 *    would let the user re-submit into a suspended entity).
 *  - `verified`   — legal entity ACTIVE (bank step unlocked, with or without an
 *    account yet).
 *  - `denied`     — provider rejected the legal entity (terminal until re-submit).
 */
export type KybStage = 'unverified' | 'pending' | 'verified' | 'denied'

export function kybStageFromOnboarding(
  status: PaymentOnboardingStatus,
): KybStage {
  switch (status) {
    // KYB_PENDING is a fresh review; SUSPENDED is a previously-active entity now
    // under review (recovers via webhook). Both render the pending/under-review
    // state and must NOT present a fresh "Start verification" CTA (EX-40413).
    case 'KYB_PENDING':
    case 'SUSPENDED':
      return 'pending'
    case 'KYB_DENIED':
      return 'denied'
    case 'KYB_ACTIVE_AWAITING_BANK':
    case 'CONNECTED':
    case 'PENDING_ACTIVATION':
      return 'verified'
    case 'NOT_CONNECTED':
    case 'DISCONNECTED':
    default:
      return 'unverified'
  }
}

/** The bank-account step is unlocked only once business verification is done. */
export function isBankStepUnlocked(status: PaymentOnboardingStatus): boolean {
  return kybStageFromOnboarding(status) === 'verified'
}

/** Narrow the discriminated connection response to its onboarding status. */
export function onboardingStatusOf(
  connection: PaymentConnectionResponse | undefined,
): PaymentOnboardingStatus {
  return connection?.onboardingStatus ?? 'NOT_CONNECTED'
}

/** The legal business name persisted on the connection (only present once a
 * legal entity exists; the backend field is nullable). */
export function legalBusinessNameOf(
  connection: PaymentConnectionResponse | undefined,
): string {
  return connection && connection.onboardingStatus !== 'NOT_CONNECTED'
    ? (connection.legalBusinessName ?? '')
    : ''
}

/** The org's linked funding account, once collected via the Account Collection
 * Flow embed and linked by `/complete`. Present only on a non-NOT_CONNECTED
 * connection that has completed a funding flow; null otherwise. Drives the Bank
 * Account card's "connected" state (EX-40413). */
export function fundingAccountOf(
  connection: PaymentConnectionResponse | undefined,
): PaymentFundingAccount | null {
  return connection && connection.onboardingStatus !== 'NOT_CONNECTED'
    ? (connection.fundingAccount ?? null)
    : null
}

/** The funding-account `status` value that means "linked and usable". A linked,
 * verified account is `VERIFIED`; every other present status remains
 * non-actionable and must not render as "Active". */
export const FUNDING_ACCOUNT_CONNECTED_STATUS = 'VERIFIED'

export type FundingAccountStage =
  | 'absent'
  | 'unverified'
  | 'pending'
  | 'verified'
  | 'review'

/** Map a provider funding-account status onto the display/action contract.
 * Statuses are normalized defensively because the realtime provider projection
 * is independent from the durable connection summary. */
export function fundingAccountStageFromStatus(
  status: string | undefined,
): FundingAccountStage {
  if (!status) return 'absent'
  switch (status.toUpperCase()) {
    case FUNDING_ACCOUNT_CONNECTED_STATUS:
      return 'verified'
    case 'UNVERIFIED':
      return 'unverified'
    case 'PENDING_VERIFICATION':
      return 'pending'
    default:
      return 'review'
  }
}

/**
 * Map the opaque API status onto the complete display/action contract.
 * Collection is intentionally distinct from verification, while unknown future
 * statuses fail closed instead of re-enabling enrollment.
 */
export function fundingAccountStageOf(
  connection: PaymentConnectionResponse | undefined,
): FundingAccountStage {
  const fundingAccount = fundingAccountOf(connection)
  return fundingAccountStageFromStatus(fundingAccount?.status)
}

export function canLaunchFundingAccountFlow(
  connection: PaymentConnectionResponse | undefined,
  createPending: boolean,
  completionPending = false,
): boolean {
  return (
    fundingAccountStageOf(connection) === 'absent' &&
    !createPending &&
    !completionPending
  )
}

/**
 * Whether the org's linked funding account is verified and usable. The Bank
 * Account card renders its "Active" details off this, while the separate stage
 * mapping keeps collected and unknown accounts visible but non-actionable.
 */
export function isFundingAccountConnected(
  connection: PaymentConnectionResponse | undefined,
): boolean {
  return fundingAccountStageOf(connection) === 'verified'
}

/** Render a structured address as one readable line, or "" when empty so
 * callers can fall back to their own em-dash. */
export function formatPartyAddress(address: PartyAddress | null): string {
  if (!address) return ''
  const street = [address.line1, address.line2]
    .filter((part) => part && part.trim().length > 0)
    .join(', ')
  const cityStateZip = [
    address.locality,
    [address.region, address.postalCode]
      .filter((part) => part && part.trim().length > 0)
      .join(' '),
  ]
    .filter((part) => part && part.trim().length > 0)
    .join(', ')
  return [street, cityStateZip].filter((part) => part.length > 0).join(', ')
}
